import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Pencil, Plus, Trash2, LogOut, Users, Newspaper, X, Upload, ImagePlus, Headphones } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const EMPTY_TEAM = { name: "", role: "", registry: "", education: "", bio: "", photo: "" };
const EMPTY_ARTICLE = { title: "", summary: "", content: "", category: "", date: "", image: "" };

const inputCls =
  "w-full rounded-xl border border-cob-light bg-white px-4 py-3 text-sm text-cob-ink outline-none transition-colors focus:border-cob-green focus:ring-2 focus:ring-cob-green/15";
const labelCls = "mb-1.5 block text-[12px] font-semibold uppercase tracking-[0.14em] text-cob-muted";

const Field = ({ label, children }) => (
  <label className="block">
    <span className={labelCls}>{label}</span>
    {children}
  </label>
);

const PhotoUpload = ({ value, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`/api/files/${data.path}`);
      toast.success("Imagem enviada.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-start gap-4">
      {value ? (
        <img
          src={value}
          alt="Prévia da imagem"
          data-testid={`${testid}-preview`}
          className="h-20 w-20 shrink-0 rounded-xl border border-cob-light object-cover object-top"
        />
      ) : (
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-dashed border-cob-light bg-cob-beige/40 text-cob-medium">
          <ImagePlus className="h-6 w-6" />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <label
          className={`inline-flex cursor-pointer items-center gap-2 rounded-full border border-cob-green/40 px-4 py-2.5 text-[13px] font-semibold text-cob-green transition-colors hover:bg-cob-light/50 ${
            uploading ? "pointer-events-none opacity-60" : ""
          }`}
        >
          <Upload className="h-4 w-4" />
          {uploading ? "Enviando..." : "Enviar imagem do computador"}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={testid} />
        </label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-url`}
          className={inputCls}
          placeholder="ou cole uma URL de imagem"
        />
      </div>
    </div>
  );
};

function TeamManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_TEAM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/team").then((r) => setItems(r.data)).catch(() => toast.error("Erro ao carregar equipe"));
  }, []);

  useEffect(() => load(), [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name, role: item.role, registry: item.registry,
      education: item.education, bio: item.bio, photo: item.photo,
    });
    setShowForm(true);
  };

  const reset = () => {
    setForm(EMPTY_TEAM);
    setEditingId(null);
    setShowForm(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/team/${editingId}`, form);
        toast.success("Profissional atualizado.");
      } else {
        await api.post("/team", form);
        toast.success("Profissional adicionado.");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este profissional?")) return;
    try {
      await api.delete(`/team/${id}`);
      toast.success("Profissional removido.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="team-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-cob-dark">Equipe</h2>
        <button
          type="button"
          data-testid="team-add-button"
          onClick={() => { reset(); setShowForm(true); }}
          className="inline-flex items-center gap-2 rounded-full bg-cob-green px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-cob-dark"
        >
          <Plus className="h-4 w-4" /> Adicionar profissional
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} data-testid="team-form" className="mt-6 rounded-2xl border border-cob-light bg-white p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nome *">
              <input required value={form.name} onChange={set("name")} data-testid="team-name-input" className={inputCls} placeholder="Dr(a). Nome Sobrenome" />
            </Field>
            <Field label="Especialidade">
              <input value={form.role} onChange={set("role")} data-testid="team-role-input" className={inputCls} placeholder="Ortopedia e Traumatologia" />
            </Field>
            <Field label="Registro profissional (CRM / CREFITO)">
              <input value={form.registry} onChange={set("registry")} data-testid="team-registry-input" className={inputCls} placeholder="CRM-PI 0000 / RQE 0000" />
            </Field>
            <Field label="Formação">
              <input value={form.education} onChange={set("education")} data-testid="team-education-input" className={inputCls} placeholder="Residência em Ortopedia — UFPI" />
            </Field>
          </div>
          <Field label="Foto do profissional">
            <PhotoUpload value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} testid="team-photo-upload" />
          </Field>
          <Field label="Biografia">
            <textarea value={form.bio} onChange={set("bio")} data-testid="team-bio-input" rows={3} className={inputCls} placeholder="Breve descrição do profissional" />
          </Field>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="team-save-button" className="rounded-full bg-cob-green px-6 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-cob-dark disabled:opacity-60">
              {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Adicionar"}
            </button>
            <button type="button" onClick={reset} data-testid="team-cancel-button" className="inline-flex items-center gap-1.5 rounded-full border border-cob-light px-6 py-3 text-[13px] font-semibold text-cob-muted hover:text-cob-dark">
              <X className="h-4 w-4" /> Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`team-row-${item.id}`} className="flex items-center gap-4 rounded-2xl border border-cob-light bg-white p-4">
            {item.photo ? (
              <img src={item.photo} alt={item.name} className="h-14 w-14 rounded-xl object-cover object-top" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-cob-light/60 text-cob-medium">
                <Users className="h-6 w-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg font-semibold text-cob-dark">{item.name}</p>
              <p className="truncate text-[13px] text-cob-muted">{item.role || "—"}</p>
            </div>
            <button type="button" onClick={() => startEdit(item)} data-testid={`team-edit-${item.id}`} aria-label="Editar" className="flex h-10 w-10 items-center justify-center rounded-full border border-cob-light text-cob-green transition-colors hover:bg-cob-light/50">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => remove(item.id)} data-testid={`team-delete-${item.id}`} aria-label="Remover" className="flex h-10 w-10 items-center justify-center rounded-full border border-cob-light text-red-600 transition-colors hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="py-8 text-center text-sm text-cob-muted">Nenhum profissional cadastrado.</p>}
      </div>
    </div>
  );
}

function ArticleManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/articles").then((r) => setItems(r.data)).catch(() => toast.error("Erro ao carregar conteúdos"));
  }, []);

  useEffect(() => load(), [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title, summary: item.summary, content: item.content,
      category: item.category, date: item.date, image: item.image,
    });
    setShowForm(true);
  };

  const reset = () => {
    setForm(EMPTY_ARTICLE);
    setEditingId(null);
    setShowForm(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/articles/${editingId}`, form);
        toast.success("Conteúdo atualizado.");
      } else {
        await api.post("/articles", form);
        toast.success("Conteúdo publicado.");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este conteúdo?")) return;
    try {
      await api.delete(`/articles/${id}`);
      toast.success("Conteúdo removido.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="article-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-cob-dark">Conteúdos</h2>
        <button
          type="button"
          data-testid="article-add-button"
          onClick={() => { reset(); setShowForm(true); }}
          className="inline-flex items-center gap-2 rounded-full bg-cob-green px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-cob-dark"
        >
          <Plus className="h-4 w-4" /> Novo conteúdo
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} data-testid="article-form" className="mt-6 rounded-2xl border border-cob-light bg-white p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Título *">
              <input required value={form.title} onChange={set("title")} data-testid="article-title-input" className={inputCls} placeholder="Título do artigo" />
            </Field>
            <Field label="Categoria">
              <input value={form.category} onChange={set("category")} data-testid="article-category-input" className={inputCls} placeholder="Ortopedia, Fisioterapia..." />
            </Field>
            <Field label="Data">
              <input value={form.date} onChange={set("date")} data-testid="article-date-input" className={inputCls} placeholder="Julho de 2026" />
            </Field>
          </div>
          <Field label="Imagem do artigo">
            <PhotoUpload value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="article-image-upload" />
          </Field>
          <Field label="Resumo">
            <textarea value={form.summary} onChange={set("summary")} data-testid="article-summary-input" rows={2} className={inputCls} placeholder="Resumo exibido no cartão" />
          </Field>
          <Field label="Conteúdo completo">
            <textarea value={form.content} onChange={set("content")} data-testid="article-content-input" rows={6} className={inputCls} placeholder="Texto completo do artigo. Separe parágrafos com linha em branco." />
          </Field>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="article-save-button" className="rounded-full bg-cob-green px-6 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-cob-dark disabled:opacity-60">
              {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Publicar"}
            </button>
            <button type="button" onClick={reset} data-testid="article-cancel-button" className="inline-flex items-center gap-1.5 rounded-full border border-cob-light px-6 py-3 text-[13px] font-semibold text-cob-muted hover:text-cob-dark">
              <X className="h-4 w-4" /> Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`article-row-${item.id}`} className="flex items-center gap-4 rounded-2xl border border-cob-light bg-white p-4">
            {item.image ? (
              <img src={item.image} alt={item.title} className="h-14 w-14 rounded-xl object-cover" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-cob-light/60 text-cob-medium">
                <Newspaper className="h-6 w-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg font-semibold text-cob-dark">{item.title}</p>
              <p className="truncate text-[13px] text-cob-muted">
                {[item.category, item.date].filter(Boolean).join(" • ") || "—"}
              </p>
            </div>
            <button type="button" onClick={() => startEdit(item)} data-testid={`article-edit-${item.id}`} aria-label="Editar" className="flex h-10 w-10 items-center justify-center rounded-full border border-cob-light text-cob-green transition-colors hover:bg-cob-light/50">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => remove(item.id)} data-testid={`article-delete-${item.id}`} aria-label="Remover" className="flex h-10 w-10 items-center justify-center rounded-full border border-cob-light text-red-600 transition-colors hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="py-8 text-center text-sm text-cob-muted">Nenhum conteúdo publicado.</p>}
      </div>
    </div>
  );
}

const EMPTY_ATTENDANT = { name: "", role: "", kind: "human", photo: "" };

function AttendantManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_ATTENDANT);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/attendants").then((r) => setItems(r.data)).catch(() => toast.error("Erro ao carregar atendentes"));
  }, []);

  useEffect(() => load(), [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, role: item.role, kind: item.kind, photo: item.photo });
    setShowForm(true);
  };

  const reset = () => {
    setForm(EMPTY_ATTENDANT);
    setEditingId(null);
    setShowForm(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/attendants/${editingId}`, form);
        toast.success("Atendente atualizado.");
      } else {
        await api.post("/attendants", form);
        toast.success("Atendente adicionado.");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este atendente?")) return;
    try {
      await api.delete(`/attendants/${id}`);
      toast.success("Atendente removido.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="attendant-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-cob-dark">Atendentes</h2>
        <button
          type="button"
          data-testid="attendant-add-button"
          onClick={() => { reset(); setShowForm(true); }}
          className="inline-flex items-center gap-2 rounded-full bg-cob-green px-5 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-cob-dark"
        >
          <Plus className="h-4 w-4" /> Adicionar atendente
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} data-testid="attendant-form" className="mt-6 rounded-2xl border border-cob-light bg-white p-6 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Nome *">
              <input required value={form.name} onChange={set("name")} data-testid="attendant-name-input" className={inputCls} placeholder="Nome" />
            </Field>
            <Field label="Função">
              <input value={form.role} onChange={set("role")} data-testid="attendant-role-input" className={inputCls} placeholder="Atendimento e Recepção" />
            </Field>
            <Field label="Tipo">
              <select value={form.kind} onChange={set("kind")} data-testid="attendant-kind-select" className={inputCls}>
                <option value="human">Atendente (pessoa)</option>
                <option value="ai">Assistente virtual (IA)</option>
              </select>
            </Field>
          </div>
          <Field label="Foto">
            <PhotoUpload value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} testid="attendant-photo-upload" />
          </Field>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="attendant-save-button" className="rounded-full bg-cob-green px-6 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-cob-dark disabled:opacity-60">
              {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Adicionar"}
            </button>
            <button type="button" onClick={reset} data-testid="attendant-cancel-button" className="inline-flex items-center gap-1.5 rounded-full border border-cob-light px-6 py-3 text-[13px] font-semibold text-cob-muted hover:text-cob-dark">
              <X className="h-4 w-4" /> Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`attendant-row-${item.id}`} className="flex items-center gap-4 rounded-2xl border border-cob-light bg-white p-4">
            {item.photo ? (
              <img src={item.photo} alt={item.name} className="h-14 w-14 rounded-xl object-cover object-top" />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-cob-light/60 font-serif text-xl font-semibold text-cob-green">
                {(item.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg font-semibold text-cob-dark">
                {item.name}
                {item.kind === "ai" && (
                  <span className="ml-2 rounded-full bg-cob-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cob-gold align-middle">IA</span>
                )}
              </p>
              <p className="truncate text-[13px] text-cob-muted">{item.role || "—"}</p>
            </div>
            <button type="button" onClick={() => startEdit(item)} data-testid={`attendant-edit-${item.id}`} aria-label="Editar" className="flex h-10 w-10 items-center justify-center rounded-full border border-cob-light text-cob-green transition-colors hover:bg-cob-light/50">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => remove(item.id)} data-testid={`attendant-delete-${item.id}`} aria-label="Remover" className="flex h-10 w-10 items-center justify-center rounded-full border border-cob-light text-red-600 transition-colors hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="py-8 text-center text-sm text-cob-muted">Nenhum atendente cadastrado.</p>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("team");

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cob-beige/60" data-testid="admin-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-cob-light border-t-cob-green" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-cob-beige/60" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-cob-light/70 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between px-4 sm:px-6">
          <Logo compact />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-[13px] text-cob-muted">{user.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-cob-light px-4 py-2 text-[13px] font-semibold text-cob-dark transition-colors hover:border-red-200 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTab("team")}
            data-testid="tab-team"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors ${
              tab === "team" ? "bg-cob-dark text-white" : "border border-cob-light bg-white text-cob-muted hover:text-cob-dark"
            }`}
          >
            <Users className="h-4 w-4" /> Equipe
          </button>
          <button
            type="button"
            onClick={() => setTab("articles")}
            data-testid="tab-articles"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors ${
              tab === "articles" ? "bg-cob-dark text-white" : "border border-cob-light bg-white text-cob-muted hover:text-cob-dark"
            }`}
          >
            <Newspaper className="h-4 w-4" /> Conteúdos
          </button>
          <button
            type="button"
            onClick={() => setTab("attendants")}
            data-testid="tab-attendants"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors ${
              tab === "attendants" ? "bg-cob-dark text-white" : "border border-cob-light bg-white text-cob-muted hover:text-cob-dark"
            }`}
          >
            <Headphones className="h-4 w-4" /> Atendentes
          </button>
        </div>

        <div className="mt-8">
          {tab === "team" ? <TeamManager /> : tab === "articles" ? <ArticleManager /> : <AttendantManager />}
        </div>
      </main>
    </div>
  );
}
